import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { DBAccount } from "../../../../api/types";
import { ProprAccountOption, useAPI } from "../../../../api/useAPI";
import ProprAccountPicker, {
  isTerminalProprStatus,
} from "../../../../components/ProprAccountPicker";

interface Props {
  open: boolean;
  handleClose: () => void;
  account: DBAccount;
  reloadAccounts: () => void;
}

/**
 * Pick which Propr account this bot account trades on.
 *
 * Two kinds of account can appear here:
 *   - challenge (paper)  — simulated funds, from /challenge-attempts
 *   - funded (b_book/a_book) — REAL capital, from /book-account-issuances
 *
 * Propr provisions a new accountId per challenge attempt and again when an
 * account becomes funded, so the stored proprAccountId goes stale on those
 * events and has to be re-pointed. Propr's "archived" flag is dashboard-only
 * and is NOT exposed by the API, so the challenge, created date and balance
 * are what identify an account here.
 *
 * Selectability rules live in ProprAccountPicker (shared with the add-profile
 * form) so the two screens cannot drift apart.
 */
export default function ProprAccountModal({
  open,
  handleClose,
  account,
  reloadAccounts,
}: Props) {
  const { getProprAccounts, setProprAccountId } = useAPI();
  const [options, setOptions] = useState<ProprAccountOption[]>([]);
  const [selected, setSelected] = useState<string>(account.proprAccountId ?? "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setSuccess("");
    setSelected(account.proprAccountId ?? "");

    getProprAccounts(account._id.toString())
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.success) {
          setOptions(result.data);
        } else {
          setError(result.error || "Failed to load Propr accounts");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "An error occurred");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account._id, account.proprAccountId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const result = await setProprAccountId(account._id.toString(), selected);

      if (!result.success) {
        setError(result.error || "Failed to update the Propr account");
        return;
      }

      setSuccess("Propr account updated");
      reloadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  }, [account._id, selected, setProprAccountId, reloadAccounts]);

  const isUnchanged = selected === (account.proprAccountId ?? "");

  const currentOption = options.find(
    (option) => option.accountId === account.proprAccountId,
  );
  const selectedOption = options.find((option) => option.accountId === selected);

  // The bot is pointed at an account that has since died — it cannot trade
  // until a different one is selected.
  const currentIsDead =
    !!currentOption && isTerminalProprStatus(currentOption.status);

  // Switching to a funded account means the bot starts trading REAL capital.
  const switchingToFunded = !isUnchanged && selectedOption?.source === "funded";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Propr account - {account.name}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" variant="body1" sx={{ mb: 2 }}>
          Select which Propr account this bot trades on. Propr creates a new
          account per challenge attempt, and again once an account becomes
          funded. Its "archived" flag is not exposed by the API, so use the
          challenge, created date and balance to identify the right one. Failed
          and closed accounts can no longer trade and cannot be selected.
        </Typography>

        {currentIsDead && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The account this bot is currently using is{" "}
            <strong>{currentOption?.status}</strong>. It can no longer trade —
            select another account below.
          </Alert>
        )}

        {switchingToFunded && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You are selecting a <strong>funded</strong> account. This trades{" "}
            <strong>real capital</strong>, not simulated funds. Orders are also
            rejected until KYC is complete.
          </Alert>
        )}

        {loading && (
          <Box sx={{ textAlign: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && options.length === 0 && error === "" && (
          <Alert severity="info">
            No Propr accounts found for this API key.
          </Alert>
        )}

        {!loading && options.length > 0 && (
          <ProprAccountPicker
            options={options}
            selected={selected}
            onSelect={setSelected}
            currentAccountId={account.proprAccountId}
          />
        )}

        {error !== "" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success !== "" && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || !selected || isUnchanged}
          variant="contained"
          color={switchingToFunded ? "error" : "primary"}
        >
          {switchingToFunded ? "Use real-money account" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
